import { Component, ViewChild } from '@angular/core'
import { NgbNav } from '@ng-bootstrap/ng-bootstrap'
import { TranslateService } from '@ngx-translate/core'
import { APIService } from 'api/module'
import { BaseComponent } from 'common/helpers'
import { MessageBoxService } from 'common/util/module'
import { CustomField, UnfilteredTag } from 'elements-sdk'
import { File, UploaderService } from 'main/services/uploader.service'
import { CustomFieldsService } from 'media.customFields'

@Component({
  selector: 'uploader-panel',
  templateUrl: './uploaderPanel.component.pug',
  styleUrls: ['./uploaderPanel.component.scss'],
})
export class UploaderPanelComponent extends BaseComponent {
  globalMetadata: Record<string, any> = {}
  metadata: Record<string, Record<string, any>|undefined> = {}
  globalTags: UnfilteredTag[] = []
  tags: Record<string, UnfilteredTag[]|undefined> = {}
  selectedFile: File|null = null
  visible = false

  @ViewChild(NgbNav) tabs: NgbNav

  Math = Math

  constructor (
    private api: APIService,
    private messageBox: MessageBoxService,
    private translate: TranslateService,
    private customFields: CustomFieldsService,
    public uploader: UploaderService,
  ) {
    super()
  }

  ngOnInit () {
    this.subs.add(
      this.uploader.filesAdded$.subscribe(async (files) => {
        for (const file of files) {
          this.metadata[file.fullPath] ??= {}
        }
        if (!this.haveFields()) {
          this.startAll(files)
        } else {
          this.visible = true
          this.tabs.select('global')
        }
      }),
    )

    this.subs.add(
      this.uploader.complete$.subscribe(() => {
        this.visible = false
      }),
    )

    this._clear()
  }

  _clear () {
    this.selectedFile = null
    this.uploader.clear()
    this.metadata = {}
    this.globalMetadata = {}
    this.globalTags = []
    this.tags = {}
    this.visible = false
  }

  async clear () {
    if ([this.uploader.state.UPLOADING, this.uploader.state.PAUSED].includes(this.uploader.getState())) {
      try {
        await this.messageBox.show({
          text: this.translate.instant('Abort current uploads?'),
          positive: this.translate.instant('Abort'),
          negative: this.translate.instant('Don\'t abort'),
          danger: true,
        })
      } catch {
        return
      }
    }
    this._clear()
  }

  selectFile (file: File) {
    // Save current file fields first
    if (this.selectedFile) {
      for (const field of this.uploader.mediaRoot?.customFields ?? []) {
        const fileMetadata = this.metadata[this.selectedFile.fullPath]
        if (fileMetadata) {
          const value = fileMetadata[field.name!]
          if (value) {
            this.customFields.registerAutocompletion(field as any, value)
          }
        }
      }
    }
    this.selectedFile = file
    this.tabs.select('file')
  }

  startFile (file: File) {
    file.resume()
    file.pending = false

    if (!this.uploader.permission?.allowWriteDb) {
      return
    }

    const metadata = this.customFields.combine(
      this.globalMetadata,
      this.metadata[file.fullPath],
    )
    const tags = [
      ...this.globalTags,
      ...this.tags[file.fullPath] ?? [],
    ]

    this.api.main.registerUploadMetadata({
      registerUploadMetadataEndpointRequest: {
        items: [{
          path: file.fullPath,
          customFields: metadata,
          tags: tags as any,
        }],
      },
    })
    for (const field of this.uploader.mediaRoot?.customFields ?? []) {
      const value = this.globalMetadata[field.name!]
      if (value) {
        this.customFields.registerAutocompletion(field as CustomField, value)
      }
    }
  }

  startAll (files) {
    for (const file of files || this.uploader.flow.files) {
      if (file.paused && this.haveNecessaryMetadataFor(file)) {
        this.startFile(file)
      }
    }
  }

  haveAllNecessaryMetadata () {
    if (!this.haveFields()) {
      return true
    }
    return this.uploader.flow.files.every(x => this.haveNecessaryMetadataFor(x))
  }

  haveNecessaryMetadataFor (file: File) {
    if (!this.haveFields()) {
      return true
    }
    return !this.uploader.mediaRoot?.customFields!.some(f => {
      return f.requireToUpload && !this.globalMetadata[f.name!] && !this.metadata[file.fullPath]?.[f.name!]
    })
  }

  haveFields () {
    if (!this.uploader.permission?.allowWriteDb) {
      return false
    }
    for (const field of this.uploader.mediaRoot?.customFields ?? []) {
      if (field.useForUploads) {
        return true
      }
    }
    return false
  }

  allFilesDone () {
    return this.uploader.flow.files.every(x => x.done)
  }

  haveErrors () {
    return this.uploader.flow.files.some(x => x.error)
  }
}
